import React, { useState, useEffect } from 'react';
import { X, ShieldCheck, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';

/**
 * Medical Disclaimer Banner Component
 * Dismissible banner that appears at the top of health-related pages
 * Reappears after 30 days or on new session
 */
const MedicalDisclaimerBanner = ({ page = 'general' }) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Check if user has dismissed this banner
    const dismissedDate = localStorage.getItem('healthloop_disclaimer_dismissed');
    
    if (!dismissedDate) {
      // Never dismissed, show banner
      setIsVisible(true);
    } else {
      // Check if 30 days have passed
      const daysSinceDismissed = Math.floor(
        (Date.now() - new Date(dismissedDate).getTime()) / (1000 * 60 * 60 * 24)
      );
      
      if (daysSinceDismissed >= 30) {
        // Show again after 30 days
        setIsVisible(true);
      }
    }
  }, []);

  const handleDismiss = () => {
    setIsVisible(false);
    localStorage.setItem('healthloop_disclaimer_dismissed', new Date().toISOString());
  };

  if (!isVisible) return null;

  return (
    <div
      className="bg-gradient-to-r from-blue-50 to-indigo-50 border-b-2 border-blue-200"
      data-testid="medical-disclaimer-banner"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
        <div className="flex items-start gap-3">
          {/* Icon */}
          <div className="p-2 bg-white rounded-lg shadow-sm flex-shrink-0">
            <ShieldCheck className="h-5 w-5 text-blue-600" />
          </div>

          {/* Content — explicit about the four required points: educational
              purpose, no diagnosis, no emergency service, and where to
              escalate. Tone stays calm and supportive (NHS-style) so it
              never alarms a healthy user. */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <p className="text-sm font-semibold text-blue-900 mb-1">
                  HealthLoop is here to support your preventive wellness journey
                </p>
                <p className="text-xs text-blue-800 leading-relaxed">
                  We provide <span className="font-medium">preventive health education</span>{' '}
                  and <span className="font-medium">general wellness guidance</span> grounded in
                  WHO, NHS and CDC sources. HealthLoop is{' '}
                  <span className="font-medium">not a diagnostic platform</span> and{' '}
                  <span className="font-medium">not an emergency medical service</span>.
                  For diagnosis, treatment, or anything urgent, please contact a qualified
                  healthcare provider — or your local emergency service
                  (<span className="whitespace-nowrap">UK 999</span>{' '}
                  / <span className="whitespace-nowrap">US 911</span>{' '}
                  / <span className="whitespace-nowrap">EU 112</span>).
                </p>
              </div>

              {/* Dismiss Button */}
              <Button
                variant="ghost"
                size="sm"
                onClick={handleDismiss}
                className="text-blue-700 hover:text-blue-900 hover:bg-blue-100 flex-shrink-0 -mt-1"
                aria-label="Dismiss disclaimer"
                data-testid="medical-disclaimer-dismiss"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MedicalDisclaimerBanner;
